import React, { useState, useRef, useEffect, useCallback, useMemo, memo } from 'react';
import api from '../services/api';
import FormularioDownloadButton from '../components/FormularioDownloadButton';
import { useTheme } from '../contexts/ThemeContext';

interface TemaTokens {
  fundo: string;
  fundoSidebar: string;
  fundoHeader: string;
  borda: string;
  bordaInput: string;
  texto: string;
  textoSecundario: string;
  textoTerciario: string;
  bolhaAssistente: string;
  bolhaAssistenteBorda: string;
  cardFundo: string;
  cardBorda: string;
  inputFundo: string;
  botaoAnexoFundo: string;
  chipFundo: string;
  chipBorda: string;
  scrollbarThumb: string;
  scrollbarThumbHover: string;
  hoverItem: string;
}

function obterTokens(tema: 'claro' | 'escuro'): TemaTokens {
  if (tema === 'claro') {
    return {
      fundo: '#f5f5f5',
      fundoSidebar: '#ffffff',
      fundoHeader: '#ffffff',
      borda: '#e5e5e5',
      bordaInput: '#dcdcdc',
      texto: '#1a1a1a',
      textoSecundario: '#555',
      textoTerciario: '#999',
      bolhaAssistente: '#ffffff',
      bolhaAssistenteBorda: '#e5e5e5',
      cardFundo: '#ffffff',
      cardBorda: '#e0e0e0',
      inputFundo: '#ffffff',
      botaoAnexoFundo: '#f0f0f0',
      chipFundo: '#f0f0f0',
      chipBorda: '#ddd',
      scrollbarThumb: '#ccc',
      scrollbarThumbHover: '#aaa',
      hoverItem: 'rgba(0,0,0,0.04)',
    };
  }
  return {
    fundo: '#111',
    fundoSidebar: '#1a1a1a',
    fundoHeader: '#161616',
    borda: '#2a2a2a',
    bordaInput: '#2a2a2a',
    texto: '#fff',
    textoSecundario: '#bbb',
    textoTerciario: '#555',
    bolhaAssistente: '#1e1e1e',
    bolhaAssistenteBorda: '#2a2a2a',
    cardFundo: '#1a1a1a',
    cardBorda: '#333',
    inputFundo: '#1e1e1e',
    botaoAnexoFundo: '#222',
    chipFundo: '#222',
    chipBorda: '#333',
    scrollbarThumb: '#3a3a3a',
    scrollbarThumbHover: '#666',
    hoverItem: 'rgba(255,255,255,0.04)',
  };
}

interface Anexo {
  nome: string;
  tipo: string;
  url: string; // data URL para imagens, '' para outros
}

interface Mensagem {
  role: 'user' | 'assistant';
  content: string;
  anexo?: Anexo;
}

interface Conversa {
  id: number;
  titulo: string;
  atualizado_em: string;
}

interface TarefaDados {
  titulo: string;
  descricao: string;
  prioridade: string;
  responsavel_nome: string;
  data_vencimento: string;
  quer_anexar?: boolean;
}

interface MensagemParsed {
  texto: string;
  tarefa: TarefaDados | null;
}

function parseFormulario(content: string): { texto: string; nomeFormulario: string | null } {
  const match = content.match(/\[FORMULARIO\]([\s\S]*?)\[\/FORMULARIO\]/);
  if (!match) return { texto: content, nomeFormulario: null };
  const texto = content.replace(/\[FORMULARIO\][\s\S]*?\[\/FORMULARIO\]/, '').trim();
  try {
    const dados = JSON.parse(match[1].trim());
    return { texto, nomeFormulario: dados.nome || null };
  } catch {
    return { texto: content, nomeFormulario: null };
  }
}

function parseMensagem(content: string): MensagemParsed {
  const match = content.match(/\[CRIAR_TAREFA\]([\s\S]*?)\[\/CRIAR_TAREFA\]/);
  if (!match) return { texto: content, tarefa: null };
  const texto = content.replace(/\[CRIAR_TAREFA\][\s\S]*?\[\/CRIAR_TAREFA\]/, '').trim();
  try {
    return { texto, tarefa: JSON.parse(match[1].trim()) };
  } catch {
    return { texto: content, tarefa: null };
  }
}

function renderMarkdown(text: string): React.ReactNode {
  return text.split('\n').map((linha, li, arr) => {
    const partes = linha.split(/\*\*(.*?)\*\*/g);
    return (
      <React.Fragment key={li}>
        {partes.map((p, i) => i % 2 === 1 ? <strong key={i}>{p}</strong> : <span key={i}>{p}</span>)}
        {li < arr.length - 1 && <br />}
      </React.Fragment>
    );
  });
}

function formatarData(iso: string): string {
  const d = new Date(iso);
  const hoje = new Date();
  const diff = Math.floor((hoje.getTime() - d.getTime()) / 86400000);
  if (diff === 0) return d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  if (diff === 1) return 'Ontem';
  if (diff < 7) return `${diff} dias atrás`;
  return d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
}

const prioridadeLabel: Record<string, string> = {
  baixa: '🟢 Baixa', media: '🟡 Média', alta: '🟠 Alta', urgente: '🔴 Urgente',
};

// ── Componente memoizado para cada item do sidebar ──
const ConversaItem = memo(({
  conv, ativa, onAbrir, onExcluir, t,
}: {
  conv: Conversa;
  ativa: boolean;
  onAbrir: (id: number) => void;
  onExcluir: (id: number, e: React.MouseEvent) => void;
  t: TemaTokens;
}) => (
  <div
    className={`bravo-ai-conv-item${ativa ? ' ativa' : ''}`}
    onClick={() => onAbrir(conv.id)}
  >
    <div className="bravo-ai-conv-titulo" style={{ fontWeight: ativa ? 600 : 400, color: ativa ? '#ff3b30' : t.textoSecundario }}>
      {conv.titulo}
    </div>
    <div style={{ fontSize: 11, color: t.textoTerciario, marginTop: 3 }}>{formatarData(conv.atualizado_em)}</div>
    <button
      className="bravo-ai-del-btn"
      onClick={e => onExcluir(conv.id, e)}
      title="Excluir"
      style={{ color: t.textoTerciario, display: 'flex' }}
      onMouseEnter={e => (e.currentTarget.style.color = '#ff3b30')}
      onMouseLeave={e => (e.currentTarget.style.color = t.textoTerciario)}
    >
      <svg xmlns="http://www.w3.org/2000/svg" width={12} height={12} viewBox="0 0 24 24" fill="none" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
      </svg>
    </button>
  </div>
));

// ── Componente memoizado para cada mensagem ──
const MensagemItem = memo(({
  msg, isUltimaTarefa, tarefaCriada, criandoTarefa,
  arquivosSelecionados, setArquivosSelecionados, fileInputRef,
  onConfirmarTarefa, t,
}: {
  msg: Mensagem;
  isUltimaTarefa: boolean;
  tarefaCriada: string | null;
  criandoTarefa: boolean;
  arquivosSelecionados: File[];
  setArquivosSelecionados: React.Dispatch<React.SetStateAction<File[]>>;
  fileInputRef: React.MutableRefObject<HTMLInputElement | null>;
  onConfirmarTarefa: (tarefa: TarefaDados) => void;
  t: TemaTokens;
}) => {
  const parsed = useMemo(() =>
    msg.role === 'assistant' ? parseMensagem(msg.content) : null,
    [msg.content, msg.role]
  );
  const parsedForm = useMemo(() =>
    msg.role === 'assistant' ? parseFormulario(parsed ? parsed.texto : msg.content) : null,
    [msg.content, msg.role, parsed]
  );
  const rendered = useMemo(() =>
    renderMarkdown(parsedForm ? parsedForm.texto : (parsed ? parsed.texto : msg.content)),
    [parsedForm, parsed, msg.content]
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: msg.role === 'user' ? 'flex-end' : 'flex-start', gap: 6 }}>

      {/* Anexo da mensagem (imagem ou badge de arquivo) */}
      {msg.anexo && (
        <div style={{ maxWidth: '75%' }}>
          {msg.anexo.url ? (
            <img
              src={msg.anexo.url}
              alt={msg.anexo.nome}
              style={{ maxWidth: '100%', maxHeight: 260, borderRadius: 12, display: 'block', objectFit: 'contain', background: t.cardFundo, border: `1px solid ${t.borda}` }}
            />
          ) : (
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: t.chipFundo, border: `1px solid ${t.chipBorda}`, borderRadius: 10, padding: '8px 12px', fontSize: 13, color: t.textoSecundario }}>
              <span style={{ fontSize: 18 }}>📄</span>
              <span style={{ maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{msg.anexo.nome}</span>
            </div>
          )}
        </div>
      )}

      <div style={{
        maxWidth: '75%',
        padding: '12px 16px',
        borderRadius: msg.role === 'user' ? '18px 18px 4px 18px' : '18px 18px 18px 4px',
        background: msg.role === 'user'
          ? 'linear-gradient(135deg, #e10600, #ff3b30)'
          : t.bolhaAssistente,
        color: msg.role === 'user' ? '#fff' : t.texto,
        fontSize: 14.5,
        lineHeight: 1.65,
        boxShadow: msg.role === 'user'
          ? '0 2px 12px rgba(225,6,0,0.35)'
          : '0 1px 6px rgba(0,0,0,0.12)',
        border: msg.role === 'assistant' ? `1px solid ${t.bolhaAssistenteBorda}` : 'none',
        wordBreak: 'break-word',
      }}>
        {rendered}
        {parsedForm?.nomeFormulario && (
          <FormularioDownloadButton nomeFormulario={parsedForm.nomeFormulario} />
        )}
      </div>

      {parsed?.tarefa && isUltimaTarefa && tarefaCriada !== 'ok' && (
        <div style={{
          width: '75%', background: t.cardFundo, border: `1px solid ${t.cardBorda}`,
          borderRadius: 14, padding: '18px', boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
        }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: '#ff3b30', marginBottom: 14, textTransform: 'uppercase', letterSpacing: 1 }}>
            ✦ Confirmar criação da tarefa
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, fontSize: 14, marginBottom: 14 }}>
            <div>
              <span style={{ color: t.textoTerciario, fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.8 }}>Título</span>
              <br /><strong style={{ color: t.texto }}>{parsed.tarefa.titulo}</strong>
            </div>
            {parsed.tarefa.descricao && (
              <div>
                <span style={{ color: t.textoTerciario, fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.8 }}>Descrição</span>
                <br /><span style={{ color: t.textoSecundario }}>{parsed.tarefa.descricao}</span>
              </div>
            )}
            <div style={{ display: 'flex', gap: 24 }}>
              <div>
                <span style={{ color: t.textoTerciario, fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.8 }}>Prioridade</span>
                <br /><span style={{ color: t.textoSecundario }}>{prioridadeLabel[parsed.tarefa.prioridade] || parsed.tarefa.prioridade}</span>
              </div>
              <div>
                <span style={{ color: t.textoTerciario, fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.8 }}>Responsável</span>
                <br /><span style={{ color: t.textoSecundario }}>{parsed.tarefa.responsavel_nome}</span>
              </div>
            </div>
            {parsed.tarefa.data_vencimento && (
              <div>
                <span style={{ color: t.textoTerciario, fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.8 }}>Vencimento</span>
                <br /><span style={{ color: t.textoSecundario }}>{parsed.tarefa.data_vencimento.split('-').reverse().join('/')}</span>
              </div>
            )}
          </div>

          {parsed.tarefa.quer_anexar && (
            <div style={{ marginBottom: 12 }}>
              <input ref={fileInputRef} type="file" multiple style={{ display: 'none' }}
                onChange={e => setArquivosSelecionados(Array.from(e.target.files || []))} />
              <button
                onClick={() => fileInputRef.current?.click()}
                style={{ width: '100%', background: t.botaoAnexoFundo, border: `1.5px dashed ${t.cardBorda}`, borderRadius: 8, color: t.textoSecundario, fontSize: 13, padding: '10px', cursor: 'pointer', marginBottom: 8 }}
              >
                📎 {arquivosSelecionados.length > 0 ? `${arquivosSelecionados.length} arquivo(s) selecionado(s)` : 'Clique para selecionar arquivos'}
              </button>
              {arquivosSelecionados.length > 0 && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  {arquivosSelecionados.map((f, idx) => (
                    <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 12, color: t.textoSecundario, background: t.botaoAnexoFundo, borderRadius: 6, padding: '4px 10px' }}>
                      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '90%' }}>📄 {f.name}</span>
                      <button onClick={() => setArquivosSelecionados(prev => prev.filter((_, fi) => fi !== idx))}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ff3b30', fontSize: 14, padding: 0, display: 'flex' }}>
                        <svg xmlns="http://www.w3.org/2000/svg" width={13} height={13} viewBox="0 0 24 24" fill="none" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {tarefaCriada?.startsWith('erro:') && (
            <div style={{ background: 'rgba(225,6,0,0.15)', border: '1px solid #e10600', borderRadius: 8, padding: '10px 12px', marginBottom: 12, fontSize: 13, color: '#ff3b30' }}>
              ⚠️ {tarefaCriada.replace('erro:', '')}
            </div>
          )}

          <button
            onClick={() => onConfirmarTarefa(parsed.tarefa!)}
            disabled={criandoTarefa}
            style={{
              width: '100%',
              background: criandoTarefa ? t.botaoAnexoFundo : 'linear-gradient(135deg, #e10600, #ff3b30)',
              border: 'none', borderRadius: 10,
              color: criandoTarefa ? t.textoTerciario : '#fff',
              fontWeight: 700, fontSize: 14, padding: '12px',
              cursor: criandoTarefa ? 'not-allowed' : 'pointer',
              boxShadow: criandoTarefa ? 'none' : '0 2px 12px rgba(225,6,0,0.4)',
              transition: 'opacity 0.15s',
            }}
          >
            {criandoTarefa ? '⏳ Criando tarefa...' : '✅ Criar Tarefa'}
          </button>
        </div>
      )}
    </div>
  );
});

const BravoAIPage: React.FC = () => {
  const { tema } = useTheme();
  const t = useMemo(() => obterTokens(tema), [tema]);
  const [conversas, setConversas] = useState<Conversa[]>([]);
  const [conversaAtualId, setConversaAtualId] = useState<number | null>(null);
  const [mensagens, setMensagens] = useState<Mensagem[]>([]);
  const [input, setInput] = useState('');
  const [carregando, setCarregando] = useState(false);
  const [carregandoConversa, setCarregandoConversa] = useState(false);
  const [criandoTarefa, setCriandoTarefa] = useState(false);
  const [tarefaCriada, setTarefaCriada] = useState<string | null>(null);
  const [arquivosSelecionados, setArquivosSelecionados] = useState<File[]>([]);
  const [anexoChat, setAnexoChat] = useState<File | null>(null);
  const [previewAnexo, setPreviewAnexo] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const anexoChatRef = useRef<HTMLInputElement | null>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get('/chat/conversas');
        if (!cancelled && res.data.success) {
          const lista: Conversa[] = res.data.conversas;
          setConversas(lista);
          if (lista.length > 0) {
            setConversaAtualId(lista[0].id);
            setCarregandoConversa(true);
            const r2 = await api.get(`/chat/conversas/${lista[0].id}`);
            if (!cancelled && r2.data.success) setMensagens(r2.data.mensagens);
            if (!cancelled) setCarregandoConversa(false);
          }
        }
      } catch {}
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    const el = messagesContainerRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [mensagens, carregando]);

  useEffect(() => {
    if (!carregandoConversa) inputRef.current?.focus({ preventScroll: true });
  }, [carregandoConversa]);

  const ultimaTarefaIdx = useMemo(() => {
    for (let i = mensagens.length - 1; i >= 0; i--) {
      if (mensagens[i].role === 'assistant' && parseMensagem(mensagens[i].content).tarefa) return i;
    }
    return -1;
  }, [mensagens]);

  const abrirConversa = useCallback(async (id: number) => {
    setCarregandoConversa(true);
    setConversaAtualId(id);
    setTarefaCriada(null);
    setArquivosSelecionados([]);
    setMensagens([]);
    try {
      const res = await api.get(`/chat/conversas/${id}`);
      if (res.data.success) setMensagens(res.data.mensagens);
    } catch {}
    setCarregandoConversa(false);
  }, []);

  const selecionarAnexoChat = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] ?? null;
    e.target.value = '';
    if (!file) return;
    setAnexoChat(file);
    if (file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = ev => setPreviewAnexo(ev.target?.result as string);
      reader.readAsDataURL(file);
    } else {
      setPreviewAnexo(null);
    }
  }, []);

  const removerAnexoChat = useCallback(() => {
    setAnexoChat(null);
    setPreviewAnexo(null);
  }, []);

  const novaConversa = useCallback(() => {
    setMensagens([]);
    setConversaAtualId(null);
    setTarefaCriada(null);
    setArquivosSelecionados([]);
    setAnexoChat(null);
    setPreviewAnexo(null);
    setTimeout(() => inputRef.current?.focus({ preventScroll: true }), 50);
  }, []);

  const excluirConversa = useCallback(async (id: number, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await api.delete(`/chat/conversas/${id}`);
      setConversas(prev => {
        const novaLista = prev.filter(c => c.id !== id);
        if (id === conversaAtualId) {
          if (novaLista.length > 0) abrirConversa(novaLista[0].id);
          else novaConversa();
        }
        return novaLista;
      });
    } catch {}
  }, [conversaAtualId, abrirConversa, novaConversa]);

  const enviarMensagem = useCallback(async () => {
    const texto = input.trim();
    if ((!texto && !anexoChat) || carregando) return;

    const arquivoAtual = anexoChat;
    const previewAtual = previewAnexo;

    const novaMensagem: Mensagem = {
      role: 'user',
      content: texto || '📎 Arquivo enviado',
      ...(arquivoAtual ? { anexo: { nome: arquivoAtual.name, tipo: arquivoAtual.type, url: previewAtual || '' } } : {}),
    };
    const novaLista = [...mensagens, novaMensagem];
    // Adiciona placeholder vazio para o assistente — será preenchido token a token
    setMensagens([...novaLista, { role: 'assistant', content: '' }]);
    setInput('');
    setAnexoChat(null);
    setPreviewAnexo(null);
    setCarregando(true);
    setTarefaCriada(null);

    try {
      const token = localStorage.getItem('bravobet_token') || '';
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

      let response: Response;
      if (arquivoAtual) {
        const formData = new FormData();
        formData.append('mensagem', texto || '📎 Arquivo enviado, analise-o.');
        formData.append('historico', JSON.stringify(novaLista.slice(-20).map(m => ({ role: m.role, content: m.content }))));
        if (conversaAtualId) formData.append('conversa_id', String(conversaAtualId));
        formData.append('arquivo', arquivoAtual);
        response = await fetch(`${apiUrl}/chat`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` },
          body: formData,
        });
      } else {
        response = await fetch(`${apiUrl}/chat`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
          body: JSON.stringify({
            mensagem: texto,
            historico: novaLista.slice(-20).map(m => ({ role: m.role, content: m.content })),
            conversa_id: conversaAtualId,
          }),
        });
      }

      if (!response.ok || !response.body) throw new Error('Stream falhou');

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let respostaCompleta = '';
      let novoConversaId: number | null = null;

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          const trimmed = line.trim();
          if (!trimmed.startsWith('data: ')) continue;
          const data = trimmed.slice(6);
          try {
            const parsed = JSON.parse(data);
            if (parsed.error) throw new Error(parsed.error);
            if (parsed.done) { novoConversaId = parsed.conversa_id; continue; }
            if (parsed.token) {
              respostaCompleta += parsed.token;
              // Oculta bloco CRIAR_TAREFA durante o streaming
              const visivel = respostaCompleta.includes('[CRIAR_TAREFA]')
                ? respostaCompleta.split('[CRIAR_TAREFA]')[0]
                : respostaCompleta;
              setMensagens(prev => {
                const updated = [...prev];
                updated[updated.length - 1] = { role: 'assistant', content: visivel };
                return updated;
              });
            }
          } catch {}
        }
      }

      // Atualiza mensagem final com conteúdo completo
      setMensagens(prev => {
        const updated = [...prev];
        updated[updated.length - 1] = { role: 'assistant', content: respostaCompleta };
        return updated;
      });

      if (novoConversaId && !conversaAtualId) {
        setConversaAtualId(novoConversaId);
        setConversas(prev => [{
          id: novoConversaId!,
          titulo: texto.slice(0, 60) + (texto.length > 60 ? '...' : ''),
          atualizado_em: new Date().toISOString(),
        }, ...prev]);
      } else if (novoConversaId) {
        setConversas(prev => {
          const atual = prev.find(c => c.id === novoConversaId);
          if (!atual) return prev;
          return [{ ...atual, atualizado_em: new Date().toISOString() }, ...prev.filter(c => c.id !== novoConversaId)];
        });
      }
    } catch {
      setMensagens(prev => {
        const updated = [...prev];
        updated[updated.length - 1] = { role: 'assistant', content: 'Não foi possível conectar à Bravo AI. Tente novamente.' };
        return updated;
      });
    } finally {
      setCarregando(false);
    }
  }, [input, carregando, mensagens, conversaAtualId, anexoChat, previewAnexo]);

  const confirmarCriarTarefa = useCallback(async (tarefa: TarefaDados) => {
    setCriandoTarefa(true);
    setTarefaCriada(null);
    try {
      const usersRes = await api.get('/usuarios/dropdown');
      const usuarios: { id: string; nome: string; departamento_id: string }[] = usersRes.data.usuarios || [];

      const responsavel = usuarios.find(u => {
        const nomeCompleto = u.nome.toLowerCase();
        const nomeBuscado = tarefa.responsavel_nome.toLowerCase();
        if (nomeCompleto.includes(nomeBuscado) || nomeBuscado.includes(nomeCompleto)) return true;
        const palavras = nomeBuscado.split(/\s+/).filter(p => p.length > 1);
        return palavras.length > 0 && palavras.every(p => nomeCompleto.includes(p));
      });

      if (!responsavel) {
        setTarefaCriada(`erro:Responsável "${tarefa.responsavel_nome}" não encontrado. Verifique o nome e tente novamente.`);
        return;
      }

      const tarefaRes = await api.post('/tarefas', {
        titulo: tarefa.titulo,
        descricao: tarefa.descricao || '',
        prioridade: tarefa.prioridade,
        responsavel_id: responsavel.id,
        departamento_id: responsavel.departamento_id,
        data_vencimento: tarefa.data_vencimento || null,
        status: 'Solicitado',
      });

      if (arquivosSelecionados.length > 0) {
        const tarefaId = tarefaRes.data?.tarefa?.id || tarefaRes.data?.id;
        if (tarefaId) {
          for (const arquivo of arquivosSelecionados) {
            const formData = new FormData();
            formData.append('arquivo', arquivo);
            await api.post(`/anexos/tarefa/${tarefaId}`, formData, {
              headers: { 'Content-Type': 'multipart/form-data' },
            });
          }
        }
      }

      setTarefaCriada('ok');
      setArquivosSelecionados([]);
      const msgAnexos = arquivosSelecionados.length > 0 ? ` com ${arquivosSelecionados.length} anexo(s)` : '';
      setMensagens(prev => [...prev, {
        role: 'assistant',
        content: `✅ Tarefa **"${tarefa.titulo}"** criada com sucesso${msgAnexos} e enviada para **${tarefa.responsavel_nome}**!`,
      }]);
    } catch (err: any) {
      setTarefaCriada(`erro:${err?.response?.data?.message || 'Erro ao criar tarefa.'}`);
    } finally {
      setCriandoTarefa(false);
    }
  }, [arquivosSelecionados]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      enviarMensagem();
    }
  }, [enviarMensagem]);

  const handlePaste = useCallback((e: React.ClipboardEvent<HTMLTextAreaElement>) => {
    const items = e.clipboardData?.items;
    if (!items) return;
    for (let i = 0; i < items.length; i++) {
      if (items[i].type.startsWith('image/')) {
        e.preventDefault();
        const file = items[i].getAsFile();
        if (!file) continue;
        setAnexoChat(file);
        const reader = new FileReader();
        reader.onload = ev => setPreviewAnexo(ev.target?.result as string);
        reader.readAsDataURL(file);
        break;
      }
    }
  }, []);

  return (
    <div style={{ display: 'flex', height: 'calc(100vh - 120px)', background: t.fundo, borderRadius: 16, overflow: 'hidden', boxShadow: '0 8px 40px rgba(0,0,0,0.18)' }}>

      {/* ── Sidebar de conversas ── */}
      <div style={{ width: 280, flexShrink: 0, display: 'flex', flexDirection: 'column', background: t.fundoSidebar, borderRight: `1px solid ${t.borda}` }}>
        <div style={{ padding: '20px 16px 16px', borderBottom: `1px solid ${t.borda}` }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
            <div style={{
              width: 46, height: 46, borderRadius: '50%',
              overflow: 'hidden',
              boxShadow: '0 0 16px rgba(225,6,0,0.5)',
              flexShrink: 0,
            }}>
              <img src="/bravo-ai-avatar.png" alt="Bravo AI" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            </div>
            <div>
              <div style={{ color: t.texto, fontWeight: 800, fontSize: 18, letterSpacing: 0.5 }}>Bravo AI</div>
              <div style={{ color: t.textoSecundario, fontSize: 12 }}>Assistente da Bravo.Bet</div>
            </div>
          </div>
          <button
            onClick={novaConversa}
            style={{
              width: '100%',
              background: 'linear-gradient(135deg, #e10600, #ff3b30)',
              border: 'none', borderRadius: 10, color: '#fff',
              fontWeight: 700, fontSize: 13, padding: '10px 12px',
              cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
              boxShadow: '0 2px 12px rgba(225,6,0,0.4)',
              transition: 'opacity 0.15s',
            }}
            onMouseEnter={e => (e.currentTarget.style.opacity = '0.85')}
            onMouseLeave={e => (e.currentTarget.style.opacity = '1')}
          >
            <span style={{ fontSize: 16 }}>＋</span> Nova Conversa
          </button>
        </div>

        <div className="bravo-ai-sidebar-list" style={{ flex: 1, overflowY: 'auto', overscrollBehavior: 'contain' }}>
          {conversas.length === 0 && (
            <div style={{ padding: '28px 16px', fontSize: 13, color: t.textoTerciario, textAlign: 'center', lineHeight: 1.7 }}>
              Nenhuma conversa ainda.<br />Comece uma nova!
            </div>
          )}
          {conversas.map(conv => (
            <ConversaItem
              key={conv.id}
              conv={conv}
              ativa={conversaAtualId === conv.id}
              onAbrir={abrirConversa}
              onExcluir={excluirConversa}
              t={t}
            />
          ))}
        </div>
      </div>

      {/* ── Área principal do chat ── */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', background: t.fundo }}>
        <div style={{ padding: '14px 20px', borderBottom: `1px solid ${t.borda}`, display: 'flex', alignItems: 'center', gap: 10, background: t.fundoHeader, flexShrink: 0 }}>
          <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#2ecc71', boxShadow: '0 0 6px #2ecc71' }} />
          <span style={{ color: t.textoSecundario, fontSize: 13, fontWeight: 500 }}>
            {conversaAtualId ? 'Conversa em andamento' : 'Nova conversa'}
          </span>
        </div>

        <div ref={messagesContainerRef} className="bravo-ai-messages" style={{ flex: 1, overflowY: 'auto', padding: '24px 28px', display: 'flex', flexDirection: 'column', gap: 16, overscrollBehavior: 'contain' }}>
          {carregandoConversa && (
            <div style={{ textAlign: 'center', color: t.textoTerciario, marginTop: 60, fontSize: 14 }}>Carregando conversa...</div>
          )}

          {!carregandoConversa && mensagens.length === 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', marginTop: 80, lineHeight: 1.8 }}>
              <img src="/bravo-ai-avatar.png" alt="Bravo AI" style={{ width: 120, height: 120, borderRadius: '50%', objectFit: 'cover', marginBottom: 20, boxShadow: '0 0 28px rgba(225,6,0,0.5)' }} />
              <div style={{ fontWeight: 800, color: t.texto, fontSize: 20, marginBottom: 6, textAlign: 'center' }}>Olá! Sou a Bravo AI.</div>
              <div style={{ color: t.textoSecundario, fontSize: 14, maxWidth: 400, textAlign: 'center' }}>
                Pergunte sobre processos, tire dúvidas ou diga <span style={{ color: '#ff3b30', fontWeight: 600 }}>"quero abrir um card"</span> para criar uma tarefa por aqui.
              </div>
            </div>
          )}

          {!carregandoConversa && mensagens.map((msg, i) => (
            <MensagemItem
              key={i}
              msg={msg}
              isUltimaTarefa={i === ultimaTarefaIdx}
              tarefaCriada={tarefaCriada}
              criandoTarefa={criandoTarefa}
              arquivosSelecionados={arquivosSelecionados}
              setArquivosSelecionados={setArquivosSelecionados}
              fileInputRef={fileInputRef}
              onConfirmarTarefa={confirmarCriarTarefa}
              t={t}
            />
          ))}

          {carregando && (
            <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
              <div style={{ padding: '12px 18px', borderRadius: '18px 18px 18px 4px', background: t.bolhaAssistente, border: `1px solid ${t.bolhaAssistenteBorda}`, display: 'flex', gap: 6, alignItems: 'center' }}>
                {[0, 1, 2].map(n => (
                  <div key={n} style={{ width: 8, height: 8, borderRadius: '50%', background: '#ff3b30', animation: 'bravoAiBounce 1.2s infinite', animationDelay: `${n * 0.2}s` }} />
                ))}
              </div>
            </div>
          )}
        </div>

        <div style={{ background: t.fundoHeader, borderTop: `1px solid ${t.borda}` }}>

          {/* Preview do arquivo anexado ao chat */}
          {anexoChat && (
            <div style={{ padding: '10px 20px 0', display: 'flex', alignItems: 'center', gap: 10 }}>
              {previewAnexo ? (
                <img src={previewAnexo} alt={anexoChat.name} style={{ height: 56, width: 56, objectFit: 'cover', borderRadius: 8, border: `1px solid ${t.cardBorda}` }} />
              ) : (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: t.bolhaAssistente, border: `1px solid ${t.cardBorda}`, borderRadius: 8, padding: '6px 12px', fontSize: 13, color: t.textoSecundario }}>
                  <span>📄</span>
                  <span style={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{anexoChat.name}</span>
                </div>
              )}
              <button
                onClick={removerAnexoChat}
                style={{ background: t.borda, border: 'none', borderRadius: '50%', width: 22, height: 22, cursor: 'pointer', color: t.textoSecundario, fontSize: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}
                title="Remover arquivo"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width={11} height={11} viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          )}

          <div style={{ padding: '12px 20px', display: 'flex', gap: 8, alignItems: 'flex-end' }}>
            {/* Input oculto para anexo do chat */}
            <input
              ref={anexoChatRef}
              type="file"
              accept="image/*,.txt,.csv,.json,.md,.xml,.html,.js,.ts,.py,.sql,.log,.yaml,.yml"
              style={{ display: 'none' }}
              onChange={selecionarAnexoChat}
            />

            {/* Botão de anexo */}
            <button
              onClick={() => anexoChatRef.current?.click()}
              disabled={carregando}
              title="Anexar imagem ou arquivo de texto"
              style={{
                background: 'none', border: `1.5px solid ${t.bordaInput}`, borderRadius: 10,
                color: anexoChat ? '#ff3b30' : t.textoTerciario, cursor: carregando ? 'not-allowed' : 'pointer',
                padding: '10px 12px', fontSize: 17, transition: 'color 0.15s, border-color 0.15s',
                flexShrink: 0, lineHeight: 1,
              }}
              onMouseEnter={e => { if (!carregando) { e.currentTarget.style.color = '#ff3b30'; e.currentTarget.style.borderColor = '#ff3b30'; } }}
              onMouseLeave={e => { e.currentTarget.style.color = anexoChat ? '#ff3b30' : t.textoTerciario; e.currentTarget.style.borderColor = t.bordaInput; }}
            >
              📎
            </button>

            <textarea
              ref={inputRef}
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              onPaste={handlePaste}
              placeholder="Digite sua mensagem... (Enter para enviar, Ctrl+V para colar prints)"
              rows={1}
              style={{
                flex: 1, border: `1.5px solid ${t.bordaInput}`, borderRadius: 12,
                padding: '11px 14px', fontSize: 14.5, outline: 'none',
                resize: 'none', fontFamily: 'inherit', lineHeight: 1.5,
                maxHeight: 120, overflowY: 'auto',
                background: t.inputFundo, color: t.texto, transition: 'border-color 0.2s',
              }}
              onFocus={e => (e.target.style.borderColor = '#e10600')}
              onBlur={e => (e.target.style.borderColor = t.bordaInput)}
            />
            <button
              onClick={enviarMensagem}
              disabled={(!input.trim() && !anexoChat) || carregando}
              style={{
                background: (input.trim() || anexoChat) && !carregando ? 'linear-gradient(135deg, #e10600, #ff3b30)' : t.botaoAnexoFundo,
                border: 'none', borderRadius: 12,
                color: (input.trim() || anexoChat) && !carregando ? '#fff' : t.textoTerciario,
                cursor: (input.trim() || anexoChat) && !carregando ? 'pointer' : 'not-allowed',
                padding: '11px 18px', fontSize: 18,
                boxShadow: (input.trim() || anexoChat) && !carregando ? '0 2px 12px rgba(225,6,0,0.4)' : 'none',
                transition: 'background 0.2s', flexShrink: 0,
              }}
            >
              ➤
            </button>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes bravoAiBounce {
          0%, 80%, 100% { transform: translateY(0); opacity: 0.4; }
          40% { transform: translateY(-6px); opacity: 1; }
        }
        .bravo-ai-conv-item {
          padding: 12px 14px;
          cursor: pointer;
          border-left: 3px solid transparent;
          position: relative;
          transition: background 0.1s;
        }
        .bravo-ai-conv-item:hover {
          background: ${t.hoverItem};
        }
        .bravo-ai-conv-item.ativa {
          background: rgba(225,6,0,0.15);
          border-left-color: #ff3b30;
        }
        .bravo-ai-conv-titulo {
          font-size: 13px;
          line-height: 1.4;
          padding-right: 22px;
          overflow: hidden;
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
        }
        .bravo-ai-del-btn {
          position: absolute;
          top: 10px;
          right: 8px;
          background: none;
          border: none;
          cursor: pointer;
          font-size: 14px;
          padding: 2px;
          border-radius: 4px;
          opacity: 0;
          transition: opacity 0.1s;
        }
        .bravo-ai-conv-item:hover .bravo-ai-del-btn {
          opacity: 1;
        }
        .bravo-ai-messages,
        .bravo-ai-sidebar-list {
          scrollbar-width: thin;
          scrollbar-color: ${t.scrollbarThumb} transparent;
        }
        .bravo-ai-messages::-webkit-scrollbar,
        .bravo-ai-sidebar-list::-webkit-scrollbar { width: 5px; }
        .bravo-ai-messages::-webkit-scrollbar-track,
        .bravo-ai-sidebar-list::-webkit-scrollbar-track { background: transparent; }
        .bravo-ai-messages::-webkit-scrollbar-thumb,
        .bravo-ai-sidebar-list::-webkit-scrollbar-thumb { background: ${t.scrollbarThumb}; border-radius: 4px; }
        .bravo-ai-messages::-webkit-scrollbar-thumb:hover,
        .bravo-ai-sidebar-list::-webkit-scrollbar-thumb:hover { background: ${t.scrollbarThumbHover}; }
      `}</style>
    </div>
  );
};

export default BravoAIPage;
